/**
 * SP-API onboarding wizard — Phase 1.8 Pass 5.
 *
 * 5-step BYOK flow walking the user through becoming their OWN SP-API
 * developer (Amazon's customer-as-developer path). We never offer to
 * register Sourcito as the developer of record — see CLAUDE.md hard rule:
 * BYOK everywhere, no exceptions.
 *
 * Step state is derived from /sp-api/status:
 *   not_started        → step 1 active
 *   awaiting_approval  → step 2 active
 *   failed             → step 4 active (form opens with error banner)
 *   connected          → step 5 (verified) — Verify + Disconnect CTAs
 */
import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Check, ChevronLeft, ExternalLink, Loader2 } from 'lucide-react';
import { Badge, Button, Card, Input, Label } from '../design';
import { useCreateCredential } from '../lib/credentials';
import { useMarkSpApiAwaitingApproval, useSpApiStatus, useVerifySpApi } from '../lib/spApi';

type WizardStep = 1 | 2 | 3 | 4 | 5;

function deriveStep(
  status: 'not_started' | 'awaiting_approval' | 'connected' | 'failed' | undefined,
  credentialPresent: boolean
): WizardStep {
  if (!status || status === 'not_started') return 1;
  if (status === 'awaiting_approval') return credentialPresent ? 4 : 2;
  if (status === 'connected') return 5;
  if (status === 'failed') return credentialPresent ? 5 : 4;
  return 1;
}

export function SpApiWizardPage() {
  const status = useSpApiStatus();
  const [activeStep, setActiveStep] = useState<WizardStep>(1);

  // Sync the active step with the server's onboarding status the first time
  // status loads — but keep the user's manual stepper clicks afterwards.
  const [syncedFromServer, setSyncedFromServer] = useState(false);
  useEffect(() => {
    if (status.data && !syncedFromServer) {
      setActiveStep(deriveStep(status.data.status, status.data.credentialPresent));
      setSyncedFromServer(true);
    }
  }, [status.data, syncedFromServer]);

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <header className="border-b border-border-subtle">
        <div className="max-w-3xl mx-auto px-8 py-4 flex items-center gap-3">
          <Link
            to="/credentials"
            className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
          >
            <ChevronLeft className="size-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-xl font-medium leading-tight">Connect Amazon SP-API</h1>
            <p className="text-xs text-fg-tertiary">
              You'll become your own SP-API developer. Sourcito never registers as a third-party
              developer.
            </p>
          </div>
          {status.data && (
            <Badge
              variant={
                status.data.status === 'connected'
                  ? 'positive'
                  : status.data.status === 'failed'
                    ? 'negative'
                    : status.data.status === 'awaiting_approval'
                      ? 'accent'
                      : 'neutral'
              }
              className="text-[10px] uppercase tracking-wider"
            >
              {status.data.status.replace('_', ' ')}
            </Badge>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-8 py-10 space-y-6">
        <Stepper activeStep={activeStep} setActiveStep={setActiveStep} />

        {activeStep === 1 && <Step1 onAdvance={() => setActiveStep(2)} />}
        {activeStep === 2 && <Step2 onAdvance={() => setActiveStep(3)} />}
        {activeStep === 3 && <Step3 onAdvance={() => setActiveStep(4)} />}
        {activeStep === 4 && (
          <Step4 status={status.data?.status} onAdvance={() => setActiveStep(5)} />
        )}
        {activeStep === 5 && <Step5 status={status.data?.status} />}
      </main>
    </div>
  );
}

function Stepper({
  activeStep,
  setActiveStep,
}: {
  activeStep: WizardStep;
  setActiveStep: (s: WizardStep) => void;
}) {
  const labels = [
    'Register as developer',
    'Create LWA app',
    'Generate refresh token',
    'Paste credentials',
    'Verify',
  ];
  return (
    <ol className="flex items-center gap-2 text-xs">
      {labels.map((label, i) => {
        const step = (i + 1) as WizardStep;
        const isActive = step === activeStep;
        const isPast = step < activeStep;
        return (
          <li key={label} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setActiveStep(step)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xs transition-colors duration-fast ${
                isActive
                  ? 'bg-accent/[0.18] text-accent-fg font-medium'
                  : isPast
                    ? 'text-fg-secondary hover:text-fg-primary'
                    : 'text-fg-tertiary hover:text-fg-secondary'
              }`}
            >
              <span
                className={`size-5 inline-flex items-center justify-center rounded-full text-[10px] tabular-nums ${
                  isPast
                    ? 'bg-positive/30 text-positive'
                    : isActive
                      ? 'bg-accent/30 text-accent-fg'
                      : 'bg-surface-elev text-fg-tertiary border border-border-subtle'
                }`}
              >
                {isPast ? <Check className="size-3" /> : step}
              </span>
              {label}
            </button>
            {i < labels.length - 1 && <span className="text-fg-tertiary">→</span>}
          </li>
        );
      })}
    </ol>
  );
}

function Step1({ onAdvance }: { onAdvance: () => void }) {
  const mark = useMarkSpApiAwaitingApproval();
  return (
    <Card>
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-medium">1. Register as your own SP-API developer</h2>
          <p className="text-sm text-fg-secondary mt-1">
            Sign into Seller Central → <strong>Apps & Services → Develop apps for Amazon</strong>{' '}
            and submit the developer-profile form.
          </p>
        </div>
        <p className="text-sm text-fg-secondary">
          Amazon's approval takes <strong>1–6 weeks</strong>. Sourcito works fully without SP-API
          while you wait — gating + exact-fee enrichment just doesn't fire yet.
        </p>
        <div className="rounded-md bg-surface-elev border border-border-subtle p-3 text-sm text-fg-secondary">
          <p className="font-medium text-fg-primary mb-1">What Amazon will ask</p>
          <ul className="list-disc pl-5 space-y-0.5 text-fg-secondary">
            <li>Company info + IRS taxpayer ID</li>
            <li>Data-use disclosure (you're sourcing, not redistributing)</li>
            <li>Security agreement</li>
          </ul>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="https://sellercentral.amazon.com/apps-and-services/dashboard"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-fg hover:underline"
          >
            Open Seller Central
            <ExternalLink className="size-3.5" />
          </a>
          <Button
            type="button"
            size="sm"
            loading={mark.isPending}
            onClick={async () => {
              try {
                await mark.mutateAsync();
                onAdvance();
              } catch {
                // Already-connected tenants get 409; on that case still advance
                // to whichever step the status implies.
                onAdvance();
              }
            }}
          >
            I've submitted to Amazon — continue
          </Button>
        </div>
      </div>
    </Card>
  );
}

function Step2({ onAdvance }: { onAdvance: () => void }) {
  return (
    <Card>
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-medium">2. Create your LWA application</h2>
          <p className="text-sm text-fg-secondary mt-1">
            Once Amazon approves your developer profile, create a Login-with-Amazon application from
            your developer dashboard.
          </p>
        </div>
        <div className="rounded-md bg-surface-elev border border-border-subtle p-3 text-sm">
          <p className="font-medium text-fg-primary mb-1">Steps</p>
          <ol className="list-decimal pl-5 space-y-0.5 text-fg-secondary">
            <li>In Seller Central → Develop apps, click "Add new app client".</li>
            <li>Pick "Selling Partner API" + the roles you authorized.</li>
            <li>
              Note the <strong>LWA Client ID</strong> + <strong>LWA Client Secret</strong>.
            </li>
          </ol>
        </div>
        <p className="text-xs text-fg-tertiary">
          Screenshots coming in v1.5.1 — for now follow Amazon's LWA app-creation guide.
        </p>
        <div>
          <Button type="button" onClick={onAdvance}>
            Continue
          </Button>
        </div>
      </div>
    </Card>
  );
}

function Step3({ onAdvance }: { onAdvance: () => void }) {
  return (
    <Card>
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-medium">3. Generate your refresh token</h2>
          <p className="text-sm text-fg-secondary mt-1">
            Use Amazon's LWA self-authorization flow to mint a long-lived refresh token from your
            own seller account.
          </p>
        </div>
        <div className="rounded-md bg-surface-elev border border-border-subtle p-3 text-sm">
          <p className="font-medium text-fg-primary mb-1">Self-authorization flow</p>
          <ol className="list-decimal pl-5 space-y-0.5 text-fg-secondary">
            <li>From your app's Edit page in Seller Central, click "Authorize".</li>
            <li>Approve the requested roles for your own seller account.</li>
            <li>
              Amazon redirects you to your app's URL with a long <strong>refresh_token</strong> in
              the URL.
            </li>
            <li>Copy that token — you'll paste it in the next step.</li>
          </ol>
        </div>
        <p className="text-xs text-fg-tertiary">
          The refresh token never expires unless you revoke it. Treat it like a password — Sourcito
          encrypts it with AES-GCM at rest.
        </p>
        <div>
          <Button type="button" onClick={onAdvance}>
            I have my refresh token — continue
          </Button>
        </div>
      </div>
    </Card>
  );
}

function Step4({
  status,
  onAdvance,
}: {
  status: 'not_started' | 'awaiting_approval' | 'connected' | 'failed' | undefined;
  onAdvance: () => void;
}) {
  const createCredential = useCreateCredential();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [form, setForm] = useState({
    spApiLwaClientId: '',
    spApiLwaClientSecret: '',
    spApiLwaRefreshToken: '',
    spApiSellerId: '',
    spApiMarketplaceId: 'ATVPDKIKX0DER',
  });

  function update<K extends keyof typeof form>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    try {
      await createCredential.mutateAsync({
        provider: 'sp_api',
        // `value` is required by the base zod schema; the route ignores it
        // for SP-API in favor of the dedicated fields below.
        value: 'unused',
        ...form,
      });
      onAdvance();
    } catch (err) {
      setSubmitError((err as Error).message);
    }
  }

  return (
    <Card>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <h2 className="text-lg font-medium">4. Paste your credentials</h2>
          <p className="text-sm text-fg-secondary mt-1">
            Sourcito encrypts the refresh token + client secret with AES-GCM. Seller ID +
            marketplace are stored as public metadata.
          </p>
        </div>

        {status === 'failed' && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm text-negative">
              Last verification failed. Double-check the values below and try again.
            </p>
          </Card>
        )}

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="lwaClientId">LWA Client ID</Label>
            <Input
              id="lwaClientId"
              value={form.spApiLwaClientId}
              onChange={(e) => update('spApiLwaClientId', e.target.value)}
              placeholder="amzn1.application-oa2-client.xxx"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lwaClientSecret">LWA Client Secret</Label>
            <Input
              id="lwaClientSecret"
              type="password"
              value={form.spApiLwaClientSecret}
              onChange={(e) => update('spApiLwaClientSecret', e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lwaRefreshToken">LWA Refresh Token</Label>
            <Input
              id="lwaRefreshToken"
              type="password"
              value={form.spApiLwaRefreshToken}
              onChange={(e) => update('spApiLwaRefreshToken', e.target.value)}
              placeholder="Atzr|…"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sellerId">Seller ID</Label>
            <Input
              id="sellerId"
              value={form.spApiSellerId}
              onChange={(e) => update('spApiSellerId', e.target.value)}
              placeholder="A1B2C3D4SELLER"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="marketplaceId">Marketplace ID</Label>
            <Input
              id="marketplaceId"
              value={form.spApiMarketplaceId}
              onChange={(e) => update('spApiMarketplaceId', e.target.value)}
              required
            />
            <p className="text-xs text-fg-tertiary">
              <code className="font-mono">ATVPDKIKX0DER</code> = US (default).{' '}
              <code className="font-mono">A1F83G8C2ARO7P</code> = UK.{' '}
              <code className="font-mono">A1PA6795UKMFR9</code> = DE.
            </p>
          </div>
        </div>

        {submitError && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm text-negative">{submitError}</p>
          </Card>
        )}

        <Button type="submit" loading={createCredential.isPending}>
          Save credentials & verify
        </Button>
      </form>
    </Card>
  );
}

function Step5({
  status,
}: {
  status: 'not_started' | 'awaiting_approval' | 'connected' | 'failed' | undefined;
}) {
  const verify = useVerifySpApi();
  const isConnected = status === 'connected';

  return (
    <Card
      className={
        isConnected
          ? 'border-positive/30 bg-positive/[0.04]'
          : 'border-warning/30 bg-warning/[0.04]'
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          {isConnected ? (
            <>
              <div className="size-9 rounded-full bg-positive/20 flex items-center justify-center">
                <Check className="size-5 text-positive" />
              </div>
              <div>
                <h2 className="text-lg font-medium text-fg-primary">SP-API connected</h2>
                <p className="text-sm text-fg-secondary">
                  New runs now enrich each ASIN with gating + exact fees + live Buy Box.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="size-9 rounded-full bg-warning/20 flex items-center justify-center">
                <Loader2 className="size-5 text-warning" />
              </div>
              <div>
                <h2 className="text-lg font-medium text-fg-primary">
                  {status === 'failed' ? 'Verification failed' : 'Awaiting verification'}
                </h2>
                <p className="text-sm text-fg-secondary">
                  Click <strong>Verify connection</strong> to re-run the LWA + Listings Restrictions
                  probe.
                </p>
              </div>
            </>
          )}
        </div>

        {isConnected && (
          <div className="rounded-md bg-surface-elev border border-border-subtle p-3 text-sm text-fg-secondary">
            <p className="font-medium text-fg-primary mb-1">What enrichment does now</p>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>Filter gated ASINs out of pending results.</li>
              <li>Replace fee-profile-estimated fees with the exact Amazon-reported fees.</li>
              <li>Show live Buy Box price + competing offer count.</li>
            </ul>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={() => verify.mutate()}
            loading={verify.isPending}
          >
            Verify connection
          </Button>
          {verify.data && !verify.data.ok && (
            <span className="text-sm text-negative">{verify.data.error}</span>
          )}
          {verify.data && verify.data.ok && (
            <span className="text-sm text-positive">Verified.</span>
          )}
        </div>
      </div>
    </Card>
  );
}
